import {
  GetSourceIdsCapableOfMedicalConditionForLocationFn,
  CheckSettingTypeCapabilityFn,
  AssociateSourceIdToLocationIdsForMedicalConditionsFn,
  SourceIdToLocationIdsAssociation,
  GetSourceIdToLocationIdsAssociationCapableOfMedicalConditionForSelectedLocationsFn,
  GetLocationIdsWithSourceIdFn,
  IsWritebackCapableSourceFn,
  GetSourceCapabilityFn,
} from './writeback-capabilities.utils.types';

/**
 * Checks if a specific setting type is capable based on the provided capability settings.
 * @param settingType - The setting type to check.
 * @param capabilitySettings - The capability settings to check against.
 */
export const checkSettingTypeCapability: CheckSettingTypeCapabilityFn = ({ settingType, capabilitySettings }) => {
  const capabilitySetting = capabilitySettings.find((c) => c.settingType === settingType);
  return !!capabilitySetting && (capabilitySetting.isCapable ?? false);
};

/**
 * @param sourceId - The ID of the source.
 * @param writebackCapabilities - The writeback capabilities for all sources in a location.
 * @returns The capabilities for the specified source.
 */
export const getSourceCapabilities: GetSourceCapabilityFn = ({ sourceId, sourceWritebackCapabilities }) => {
  const sourceCapabilitySettings = sourceWritebackCapabilities.find((s) => s.sourceId === sourceId)?.capabilities || [];
  return {
    showMedicalConditionPrimaryFields: checkSettingTypeCapability({
      capabilitySettings: sourceCapabilitySettings,
      settingType: 'medical_condition_primary_fields',
    }),
    showWritebackSettings: {
      createPerson: checkSettingTypeCapability({
        capabilitySettings: sourceCapabilitySettings,
        settingType: 'writeback_create_person',
      }),
      updatePerson: checkSettingTypeCapability({
        capabilitySettings: sourceCapabilitySettings,
        settingType: 'writeback_update_person',
      }),
      uploadDocument: checkSettingTypeCapability({
        capabilitySettings: sourceCapabilitySettings,
        settingType: 'writeback_upload_document',
      }),
    },
    showMedicalConditionSettings: checkSettingTypeCapability({
      capabilitySettings: sourceCapabilitySettings,
      settingType: 'medical_condition_tab',
    }),
    showWritebackSubmissionsTab: checkSettingTypeCapability({
      capabilitySettings: sourceCapabilitySettings,
      settingType: 'writeback_submission_tabs',
    }),
  };
};

/**
 * This function will populate the associationRef with the sourceId and locationIds that are associated
 * with the medical conditions tab.
 */
export const associateSourceIdToLocationIdsForMedicalConditions: AssociateSourceIdToLocationIdsForMedicalConditionsFn =
  ({ associationRef, sourceWritebackCapabilities, locationId }) => {
    sourceWritebackCapabilities.forEach((source) => {
      if (
        source.sourceId &&
        checkSettingTypeCapability({
          capabilitySettings: source.capabilities || [],
          settingType: 'medical_condition_tab',
        })
      ) {
        const index = associationRef.findIndex((s) => s.sourceId === source.sourceId);
        if (index !== -1) {
          associationRef[index].associatedLocationIds.push(locationId);
        } else {
          associationRef.push({
            sourceId: source.sourceId,
            associatedLocationIds: [locationId],
          });
        }
      }
    });
  };

/**
 * Returns the sourceIds capable of medical condition for a given location.
 */
export const getSourceIdsCapableOfMedicalConditionForLocation: GetSourceIdsCapableOfMedicalConditionForLocationFn = ({
  sourceWritebackCapabilities,
}) => {
  const sourceIds: string[] = [];
  sourceWritebackCapabilities.forEach((source) => {
    if (
      source.sourceId &&
      checkSettingTypeCapability({
        capabilitySettings: source.capabilities || [],
        settingType: 'medical_condition_tab',
      })
    ) {
      sourceIds.push(source.sourceId);
    }
  });

  return sourceIds;
};

/**
 * Returns the source ID -> Location IDs data capable of medical condition
 * for the provided WB capabilities map, or for the given locationIds.
 */
export const getSourceIdToLocationIdsAssociationCapableOfMedicalConditionForSelectedLocations: GetSourceIdToLocationIdsAssociationCapableOfMedicalConditionForSelectedLocationsFn =
  ({ writebackCapabilitiesMap, locationIds }) => {
    const sourceIdToLocationIdsAssociation: SourceIdToLocationIdsAssociation[] = [];
    if (locationIds) {
      for (const locationId of locationIds) {
        const locationCapabilities = writebackCapabilitiesMap[locationId];
        if (!locationCapabilities) {
          continue;
        }

        associateSourceIdToLocationIdsForMedicalConditions({
          associationRef: sourceIdToLocationIdsAssociation,
          sourceWritebackCapabilities: locationCapabilities,
          locationId,
        });
      }
    } else {
      for (const locationId in writebackCapabilitiesMap) {
        const locationCapabilities = writebackCapabilitiesMap[locationId];
        if (!locationCapabilities) {
          continue;
        }

        associateSourceIdToLocationIdsForMedicalConditions({
          associationRef: sourceIdToLocationIdsAssociation,
          sourceWritebackCapabilities: locationCapabilities,
          locationId,
        });
      }
    }

    return sourceIdToLocationIdsAssociation;
  };

/**
 * Returns the locationIds with the given sourceId.
 */
export const getLocationIdsWithSourceId: GetLocationIdsWithSourceIdFn = ({ sourceId, writebackCapabilitiesMap }) => {
  const locationIds: string[] = [];
  for (const locationId in writebackCapabilitiesMap) {
    const sources = writebackCapabilitiesMap[locationId];
    const found = sources.find((source) => source.sourceId === sourceId);
    if (found && !locationIds.includes(locationId)) {
      locationIds.push(locationId);
    }
  }

  return locationIds;
};

/**
 * Checks if a source is writeback capable.
 */
export const isWritebackCapableSource: IsWritebackCapableSourceFn = ({ sourceWritebackCapabilities, sourceId }) => {
  return sourceWritebackCapabilities.some((s) => s.sourceId === sourceId);
};
